<template>
  <b-card
    v-if="data"
    no-body
    class="monthly-earnings-card"
  >
    <b-card-header class="monthly-earnings-header">
      <h4 class="chart-title mb-0">
        Monthly Earnings
      </h4>
      <div class="header-actions">
        <small
          v-if="lastUpdatedAt"
          class="timestamp-badge"
          :class="{ 'clickable': onRefresh }"
          @click="handleRefresh"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="12"
            class="mr-1"
            :class="{ 'spin': isRefreshing }"
          />
          <span v-if="lastRefreshTimeAgo">
            {{ lastRefreshTimeAgo }}
          </span>
          <span v-else>
            {{ __dateTimeFormatterPassedTime(lastUpdatedAt) }}
          </span>
        </small>
      </div>
    </b-card-header>
    <b-card-body class="monthly-earnings-body">
      <e-charts
        ref="monthly-earnings-chart"
        autoresize
        :options="chartOptions"
        class="monthly-earnings-echart"
        auto-resize
      />
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, VBTooltip,
} from 'bootstrap-vue'
import ECharts from 'vue-echarts'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/grid'
import 'echarts/lib/chart/line'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    'e-charts': ECharts,
    BCard,
    BCardHeader,
    BCardBody,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  props: {
    data: {
      type: Object,
      default: () => {
      },
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    onRefresh: {
      type: Function,
      default: null,
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      revenue_report: {},
      currentTime: Date.now(),
      updateInterval: null,
    }
  },
  computed: {
    lastRefreshTimeAgo() {
      try {
        const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
        const lastRefreshTime = refreshTimes.monthlyearnings
        if (!lastRefreshTime) return null

        const lastRefresh = new Date(lastRefreshTime).getTime()
        const now = this.currentTime
        const diff = now - lastRefresh

        if (diff < 0) return null

        const seconds = Math.floor(diff / 1000)
        const minutes = Math.floor(seconds / 60)
        const hours = Math.floor(minutes / 60)
        const days = Math.floor(hours / 24)
        const months = Math.floor(days / 30)
        const years = Math.floor(months / 12)

        if (minutes < 1) return 'just now'
        if (minutes < 60) return `${minutes} minutes ago`
        if (hours < 24) return `${hours} hours ago`
        if (days < 30) return `${days} days ago`
        if (months < 12) return `${months} months ago`
        return `${years} years ago`
      } catch (e) {
        return null
      }
    },
    formatCategories() {
      const categories = this.data?.categories?.lables || []
      const monthMap = {
        January: 'Jan',
        February: 'Feb',
        March: 'Mar',
        April: 'Apr',
        May: 'May',
        June: 'Jun',
        July: 'Jul',
        August: 'Aug',
        September: 'Sep',
        October: 'Oct',
        November: 'Nov',
        December: 'Dec',
      }
      return categories.map(cat => {
        if (typeof cat === 'string') {
          // Handle format like "Oct 2024" or "October 2024"
          const parts = cat.split(' ')
          if (parts.length >= 2) {
            const month = parts[0]
            const year = parts[1]
            const shortMonth = monthMap[month] || month.substring(0, 3)
            return { month: shortMonth, year }
          }
          return { month: cat, year: '' }
        }
        return { month: cat, year: '' }
      })
    },
    chartOptions() {
      const categories = this.formatCategories
      const seriesData = this.data?.series?.[0]?.data || []

      // Ensure data is properly formatted as numbers
      const formattedData = seriesData.map(val => {
        const numVal = typeof val === 'string' ? parseFloat(val) : val
        return Number.isNaN(numVal) ? 0 : numVal
      })

      // Calculate min and max for y-axis
      const minVal = formattedData.length > 0 ? Math.min(...formattedData) : 0
      const maxVal = formattedData.length > 0 ? Math.max(...formattedData) : 100
      const yAxisMin = minVal >= 0 ? 0 : Math.floor(minVal * 0.9)
      const yAxisMax = maxVal === 0 ? 100 : Math.ceil(maxVal * 1.1)

      return {
        tooltip: {
          trigger: 'axis',
          formatter(params) {
            const param = params[0]
            const category = categories[param.dataIndex]
            const value = param.value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
            return `${category.month} ${category.year}<br/>${value}`
          },
          backgroundColor: '#fff',
          borderColor: '#E4E6EA',
          borderWidth: 1,
          textStyle: {
            color: '#5e5873',
            fontFamily: 'Roboto, sans-serif',
            fontSize: 12,
          },
        },
        grid: {
          left: '45px',
          right: '20px',
          top: '10px',
          bottom: '60px',
          containLabel: false,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: categories.map(cat => `${cat.month}\n${cat.year}`),
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            color: '#6E6B7B',
            fontSize: 12,
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 500,
            align: 'center',
            lineHeight: 16,
            margin: 12,
          },
        },
        yAxis: {
          type: 'value',
          scale: false,
          min: yAxisMin,
          max: yAxisMax,
          splitLine: {
            show: true,
            lineStyle: {
              color: '#E4E6EA',
              type: 'dashed',
              width: 1,
            },
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            color: '#6E6B7B',
            fontSize: 12,
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 500,
            margin: 8,
            formatter(value) {
              const numValue = Number(value)
              if (Number.isNaN(numValue)) return '0'
              // Format as whole number with comma separators
              return Math.round(numValue).toLocaleString()
            },
          },
        },
        series: [
          {
            name: 'Monthly Earnings',
            type: 'line',
            smooth: true,
            symbol: 'none',
            lineStyle: {
              color: '#4A90E2',
              width: 2,
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  {
                    offset: 0,
                    color: 'rgba(74, 144, 226, 0.35)',
                  },
                  {
                    offset: 1,
                    color: 'rgba(74, 144, 226, 0.05)',
                  },
                ],
              },
            },
            data: formattedData,
          },
        ],
      }
    },
  },
  watch: {
    data: {
      handler() {
        // Force chart update when data changes
        if (this.$refs['monthly-earnings-chart']) {
          this.$refs['monthly-earnings-chart'].resize()
        }
      },
      deep: true,
    },
  },
  mounted() {
    // Update countdown every second
    this.updateInterval = setInterval(() => {
      this.currentTime = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
  },
  methods: {
    handleRefresh() {
      if (this.onRefresh && !this.isRefreshing) {
        this.onRefresh()
      }
    },
  },
}
</script>

<style scoped>
.monthly-earnings-card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  border-radius: 0.857rem;
  overflow: hidden;
  height: 100%;
}

.monthly-earnings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: #fff;
}

.chart-title {
  font-size: 1.285rem;
  font-weight: 600;
  color: #5e5873;
  margin: 0;
  font-family: 'Roboto', sans-serif;
}

.header-actions {
  display: flex;
  align-items: center;
}

.timestamp-badge {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  color: #6e6b7b;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  font-family: 'Roboto', sans-serif;
}

.timestamp-badge.clickable {
  cursor: pointer;
  user-select: none;
}

.timestamp-badge.clickable:hover {
  background-color: #e8e8e8;
  transform: scale(1.05);
}

.timestamp-badge.clickable:active {
  transform: scale(0.98);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.monthly-earnings-body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  background: #fff;
}

.monthly-earnings-echart {
  width: 100%;
  height: 280px;
}

@media (max-width: 767px) {
  .monthly-earnings-echart {
    height: 250px;
  }
}
</style>
